/**
 * Access & Activity Report — admin reporting (super_admin / ops_admin).
 *
 * Three first-party views, all sourced from our own DB (backend
 * app/routers/access_reporting.py):
 *   • Login History  — per-login events (verification_codes: when, success, IP, device)
 *   • API Access     — authenticated session API requests (api_request_log), each
 *                      tagged source = 'frontend' (web app) or 'direct' (programmatic JWT)
 *   • Page Activity  — frontend page views / actions (frontend_activity_log)
 *   • Sign-in problems — refused sign-ins, failed 2FA codes, and password resets
 *                      with whether the email was really sent (user_activity_log).
 *                      The other tabs only see users who are already signed in,
 *                      so this is the only place "I can't log in" shows (TRUS-661).
 *
 * Filters: Organization (CBO), user, time window, and — on API Access —
 * frontend-vs-direct source. Embeddable: pass `userId` to lock the report to a
 * single user (used by the User Management per-user activity view), which hides
 * the org/user pickers.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { authFetchJson } from '@/lib/authFetch';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { trackAction } from '@/src/lib/activityTracker';
import { LogIn, Server, MousePointerClick, RefreshCw, Check, X, Loader2, ListChecks, AlertTriangle, ShieldAlert } from 'lucide-react';
import { TRACKED_ACTIONS } from '@/src/lib/trackedActions';

type ReportTab = 'logins' | 'auth' | 'api' | 'pages' | 'actions' | 'issues';
type SourceFilter = 'all' | 'frontend' | 'direct' | 'sandbox_key' | 'prod_key';

interface UserRow {
  id: number;
  name: string | null;
  email: string | null;
  role: string | null;
  last_login: string | null;
  logins: number;
  api_calls: number;
  page_events: number;
}
interface OrgRow { id: number; name: string | null; status: string | null; }
interface LoginEvent {
  user_id: number | null;
  name: string | null;
  email: string | null;
  method: string;
  occurred_at: string;
  success: boolean;
  ip_address: string | null;
  user_agent: string | null;
}
interface ApiRow {
  id: number;
  name: string | null;
  email: string | null;
  method: string;
  path: string;
  status_code: number | null;
  duration_ms: number | null;
  ip_address: string | null;
  source: string | null;
  occurred_at: string;
}
interface PageRow {
  id: number;
  name: string | null;
  email: string | null;
  event_type: string;
  path: string | null;
  page_title: string | null;
  action_name: string | null;
  occurred_at: string;
}

interface AuthEventRow {
  id: number;
  user_id?: number | null;
  name?: string | null;
  email?: string | null;
  action: string;
  details: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  same_ip?: boolean;
  same_mailbox?: boolean;
}

const LOGIN_REASONS: Record<string, string> = {
  unknown_email: 'No account has this email',
  bad_password: 'Wrong password',
  account_inactive: 'Account is not active',
  '2fa_required_no_method': '2FA required but no phone or authenticator set up',
};
const CODE_REASONS: Record<string, string> = {
  wrong_code: 'Wrong code',
  code_expired: 'Code expired',
  too_many_attempts: 'Too many attempts',
  no_code: 'No code was sent',
  challenge_rejected: 'Sign-in step expired; start again',
};
const CODE_METHODS: Record<string, string> = { sms: 'text message', authenticator: 'authenticator app', backup_code: 'backup code' };

/** One plain-language line per event: what happened, and whether it is a problem. */
function describeAuthEvent(e: AuthEventRow): { label: string; detail: string; bad: boolean } {
  const d = e.details || {};
  const sent = (ok: unknown, err: unknown) => (ok === false ? `Email NOT sent${err ? ` (${err})` : ''}` : ok === true ? 'Email sent' : '');
  switch (e.action) {
    case 'login_failed':
      return {
        label: 'Sign-in refused',
        detail: [LOGIN_REASONS[d.reason] || d.reason, d.email ? `typed ${d.email}` : ''].filter(Boolean).join(' · '),
        bad: true,
      };
    case 'login_2fa_failed':
      return { label: 'Verification code refused', detail: `${CODE_REASONS[d.reason] || d.reason} (${CODE_METHODS[d.method] || d.method})`, bad: true };
    case 'password_reset_unknown_email':
      return { label: 'Reset requested, nothing sent', detail: `No account has ${d.email || 'this email'}`, bad: true };
    case 'password_reset_requested':
      return { label: 'Reset requested', detail: sent(d.email_sent, d.error) || 'Email sent', bad: d.email_sent === false };
    case 'password_reset_email':
      return { label: 'Reset sent by an admin', detail: sent(d.email_sent, d.error), bad: d.email_sent === false };
    case 'password_reset':
      return { label: 'Password reset completed', detail: '', bad: false };
    case 'password_change':
      return { label: 'Password changed', detail: '', bad: false };
    default:
      return { label: e.action, detail: '', bad: false };
  }
}

interface IssueRow {
  id: number;
  source: string;
  status_code: number | null;
  method: string | null;
  path: string | null;
  error_type: string | null;
  error_message: string | null;
  actor_id: string | null;
  cbo_id: number | null;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  severity: string | null;
  triage_summary: string | null;
  alert_copy: string | null;
  status: string;
  rca_job_id: number | null;
  rca_status: string | null;
  rca_branch: string | null;
}
interface IssueSummary {
  total?: number; open?: number; handed_off?: number;
  critical?: number; high?: number; medium?: number; low?: number; untriaged?: number;
  occurrences?: number; last_seen_at?: string | null;
}

const DAY_OPTIONS = [1, 7, 30, 90];

// frontend_activity_log.event_type -> what the admin reads (TRUS-664).
// field_edit rows name the field only; its value is never recorded.
const EVENT_LABELS: Record<string, string> = {
  page_view: 'Opened page',
  section_view: 'Opened section',
  field_edit: 'Edited field',
  action: 'Action',
};

function fmt(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : `${ts}Z`);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function who(r: { name: string | null; email: string | null }): string {
  return r.name || r.email || 'Unknown';
}

interface AccessActivityReportProps {
  /** Lock to a single user (hides org + user pickers) — per-user view. */
  userId?: number;
  /** Lock to a single org/CBO (hides the org picker, keeps the user filter) — org-level view. */
  cboId?: number;
  /** Which sub-view to open on. Defaults to 'logins'. Deep links (e.g. the
   *  observability alert notification) pass 'issues' to land on the issue feed. */
  initialTab?: ReportTab;
  /** Sandbox Management mode: scope every view to TRIAL-status orgs only. */
  trialOnly?: boolean;
}

export default function AccessActivityReport({ userId: lockedUserId, cboId: lockedCboId, initialTab, trialOnly }: AccessActivityReportProps = {}) {
  const { t } = useAppTranslation();
  const lockedUser = typeof lockedUserId === 'number';
  const lockedCbo = typeof lockedCboId === 'number';

  const [tab, setTab] = useState<ReportTab>(initialTab ?? 'logins');
  const [days, setDays] = useState(30);
  const [userId, setUserId] = useState<number | ''>('');
  const [cboId, setCboId] = useState<number | ''>('');
  const [src, setSrc] = useState<SourceFilter>('all');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [logins, setLogins] = useState<LoginEvent[]>([]);
  const [authEvents, setAuthEvents] = useState<{ events: AuthEventRow[]; near_misses: AuthEventRow[] }>({ events: [], near_misses: [] });
  const [api, setApi] = useState<{ requests: ApiRow[]; top_endpoints: any[]; source_breakdown: any[] }>({ requests: [], top_endpoints: [], source_breakdown: [] });
  const [pages, setPages] = useState<{ events: PageRow[]; top_pages: any[] }>({ events: [], top_pages: [] });
  const [actionCatalog, setActionCatalog] = useState<any[]>([]);
  const [issues, setIssues] = useState<IssueRow[]>([]);
  const [issueSummary, setIssueSummary] = useState<IssueSummary>({});

  const effectiveUserId = lockedUser ? lockedUserId : userId;
  const effectiveCboId = lockedCbo ? lockedCboId : cboId;

  // Common filter querystring (user + org + days).
  const scopeQs = useCallback(() => {
    let qs = `days=${days}&limit=500`;
    if (effectiveUserId) qs += `&user_id=${effectiveUserId}`;
    if (effectiveCboId) qs += `&cbo_id=${effectiveCboId}`;
    if (trialOnly) qs += `&trial_only=true`;
    return qs;
  }, [days, effectiveUserId, effectiveCboId, trialOnly]);

  const loadPickers = useCallback(async () => {
    if (lockedUser) return; // per-user view needs no pickers
    try {
      const uq = effectiveCboId ? `&cbo_id=${effectiveCboId}` : '';
      const tq = trialOnly ? `&trial_only=true` : '';
      const reqs: Promise<any>[] = [
        authFetchJson<{ users: UserRow[] }>(`/api/admin/access-report/users?days=${days}&limit=500${uq}${tq}`),
      ];
      if (!lockedCbo) reqs.push(authFetchJson<{ orgs: OrgRow[] }>(`/api/admin/access-report/orgs?limit=2000${tq}`));
      const [u, o] = await Promise.all(reqs);
      setUsers(u?.users || []);
      if (o) setOrgs(o.orgs || []);
    } catch (e: any) {
      console.warn('access-report pickers load failed', e);
    }
  }, [days, effectiveCboId, lockedUser, lockedCbo, trialOnly]);

  const loadTab = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'logins') {
        const d = await authFetchJson<{ events: LoginEvent[] }>(
          `/api/admin/access-report/login-history?${scopeQs()}`,
        );
        setLogins(d.events || []);
      } else if (tab === 'auth') {
        const d = await authFetchJson<{ events: AuthEventRow[]; near_misses: AuthEventRow[] }>(
          `/api/admin/access-report/auth-events?${scopeQs()}`,
        );
        setAuthEvents({ events: d.events || [], near_misses: d.near_misses || [] });
      } else if (tab === 'api') {
        const sq = src !== 'all' ? `&source=${src}` : '';
        const d = await authFetchJson<{ requests: ApiRow[]; top_endpoints: any[]; source_breakdown: any[] }>(
          `/api/admin/access-report/api-access?${scopeQs()}${sq}`,
        );
        setApi({ requests: d.requests || [], top_endpoints: d.top_endpoints || [], source_breakdown: d.source_breakdown || [] });
      } else if (tab === 'pages') {
        const d = await authFetchJson<{ events: PageRow[]; top_pages: any[] }>(
          `/api/admin/access-report/frontend-activity?${scopeQs()}`,
        );
        setPages({ events: d.events || [], top_pages: d.top_pages || [] });
      } else if (tab === 'issues') {
        // actor_id is TEXT (Triveny UUIDs + WeaveIn int ids); map the numeric user filter to it.
        let iq = `days=${days}&limit=500`;
        if (effectiveUserId) iq += `&actor_id=${effectiveUserId}`;
        if (effectiveCboId) iq += `&cbo_id=${effectiveCboId}`;
        const [d, s] = await Promise.all([
          authFetchJson<{ issues: IssueRow[] }>(`/api/admin/observability/issues?${iq}`),
          authFetchJson<IssueSummary>(`/api/admin/observability/summary?${iq}`),
        ]);
        setIssues(d.issues || []);
        setIssueSummary(s || {});
      } else {
        const d = await authFetchJson<{ actions: any[] }>(
          `/api/admin/access-report/action-catalog?${scopeQs()}`,
        );
        setActionCatalog(d.actions || []);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [tab, scopeQs, src, days, effectiveUserId, effectiveCboId]);

  useEffect(() => { loadPickers(); }, [loadPickers]);
  useEffect(() => { loadTab(); }, [loadTab]);
  // Dogfood: opening the report is itself a tracked action.
  useEffect(() => { trackAction('access_report_viewed'); }, []);

  // "Live" — the Issues tab auto-refreshes every 20s (same polling discipline as
  // the notification bell). Only while the tab is visible and mounted.
  useEffect(() => {
    if (tab !== 'issues') return;
    const id = setInterval(() => { loadTab(); }, 20000);
    return () => clearInterval(id);
  }, [tab, loadTab]);

  const TABS: { key: ReportTab; label: string; icon: React.ReactNode }[] = [
    { key: 'logins', label: 'Login History', icon: <LogIn className="w-4 h-4" /> },
    { key: 'auth', label: 'Sign-in problems', icon: <ShieldAlert className="w-4 h-4" /> },
    { key: 'api', label: 'API Access', icon: <Server className="w-4 h-4" /> },
    { key: 'pages', label: 'Page Activity', icon: <MousePointerClick className="w-4 h-4" /> },
    { key: 'actions', label: 'Tracked Actions', icon: <ListChecks className="w-4 h-4" /> },
    { key: 'issues', label: 'Issues', icon: <AlertTriangle className="w-4 h-4" /> },
  ];

  const SOURCES: { key: SourceFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'frontend', label: 'Frontend' },
    { key: 'direct', label: 'Direct API' },
    { key: 'sandbox_key', label: 'Sandbox key' },
    { key: 'prod_key', label: 'Prod key' },
  ];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${
                tab === t.key ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Source filter — only meaningful on API Access */}
        {tab === 'api' && (
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {SOURCES.map(s => (
              <button
                key={s.key}
                onClick={() => setSrc(s.key)}
                title="Frontend = web-app calls · Direct API = programmatic calls"
                className={`px-3 py-1.5 text-sm font-medium ${
                  src === s.key ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {!lockedUser && !lockedCbo && (
            <select
              value={cboId}
              onChange={e => { setCboId(e.target.value ? Number(e.target.value) : ''); setUserId(''); }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 max-w-[16rem]"
              title="Filter by organization (CBO)"
            >
              <option value="">All organizations</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name || `CBO ${o.id}`}</option>)}
            </select>
          )}
          {!lockedUser && (
            <select
              value={userId}
              onChange={e => setUserId(e.target.value ? Number(e.target.value) : '')}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 max-w-[16rem]"
              title="Filter by user"
            >
              <option value="">All users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {who(u)} {u.email && u.name ? `(${u.email})` : ''}
                </option>
              ))}
            </select>
          )}
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          >
            {DAY_OPTIONS.map(d => <option key={d} value={d}>Last {d}d</option>)}
          </select>
          <button
            onClick={loadTab}
            disabled={loading}
            className="flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
      )}

      {/* Login History */}
      {tab === 'logins' && (
        <Table
          empty="No login events in this window."
          head={['User', 'When', 'Method', 'Result', 'IP', 'Device']}
          rows={logins.map((e, i) => [
            who(e),
            fmt(e.occurred_at),
            e.method || 'Login',
            <span key={i} className={`inline-flex items-center gap-1 ${e.success ? 'text-green-600' : 'text-gray-400'}`}>
              {e.success ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              {e.success ? 'Success' : 'Pending/failed'}
            </span>,
            e.ip_address || '—',
            <span key={`ua${i}`} className="text-xs text-gray-500 truncate block max-w-[18rem]" title={e.user_agent || ''}>
              {e.user_agent || '—'}
            </span>,
          ])}
        />
      )}

      {/* Sign-in problems */}
      {tab === 'auth' && (
        <>
          {authEvents.near_misses.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-sm font-semibold text-amber-900">
                {t('admin.auth_near_miss_title', 'Attempts with an email that matches no account')}
              </div>
              <p className="text-xs text-amber-800 mt-0.5 mb-2">
                {t(
                  'admin.auth_near_miss_hint',
                  'Probably this person using the wrong email address: same network they usually sign in from, or the same name before the @.',
                )}
              </p>
              <Table
                empty=""
                head={['When', 'What happened', 'Email typed', 'Why it matched', 'IP']}
                rows={authEvents.near_misses.map(e => [
                  fmt(e.created_at),
                  describeAuthEvent(e).label,
                  <span className="font-mono text-xs">{e.details?.email || '—'}</span>,
                  [e.same_ip ? 'same IP' : '', e.same_mailbox ? 'same name before @' : ''].filter(Boolean).join(' · '),
                  e.ip_address || '—',
                ])}
              />
            </div>
          )}
          <Table
            empty="No refused sign-ins or password resets in this window."
            head={['User', 'When', 'What happened', 'Detail', 'IP', 'Device']}
            rows={authEvents.events.map((e, i) => {
              const info = describeAuthEvent(e);
              return [
                who({ name: e.name ?? null, email: e.email ?? null }),
                fmt(e.created_at),
                <span key={i} className={`inline-flex items-center gap-1 ${info.bad ? 'text-red-600' : 'text-green-600'}`}>
                  {info.bad ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                  {info.label}
                </span>,
                info.detail || '—',
                e.ip_address || '—',
                <span key={`ua${i}`} className="text-xs text-gray-500 truncate block max-w-[18rem]" title={e.user_agent || ''}>
                  {e.user_agent || '—'}
                </span>,
              ];
            })}
          />
        </>
      )}

      {/* API Access */}
      {tab === 'api' && (
        <>
          {api.source_breakdown.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {api.source_breakdown.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                  <SourceBadge source={b.source} />
                  <span className="font-semibold text-gray-900">{b.calls}</span>
                  <span className="text-gray-500">calls</span>
                </span>
              ))}
            </div>
          )}
          {api.top_endpoints.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Top endpoints</div>
              <div className="flex flex-wrap gap-2">
                {api.top_endpoints.map((t, i) => (
                  <span key={i} className="text-xs bg-gray-100 rounded-full px-2.5 py-1 text-gray-700">
                    <span className="font-mono">{t.method}</span> {t.path} · {t.calls} · {t.avg_ms ?? '—'}ms
                  </span>
                ))}
              </div>
            </div>
          )}
          <Table
            empty="No matching API requests in this window."
            head={['User', 'When', 'Source', 'Method', 'Path', 'Status', 'ms', 'IP']}
            rows={api.requests.map(r => [
              who(r),
              fmt(r.occurred_at),
              <SourceBadge source={r.source} />,
              <span className="font-mono text-xs">{r.method}</span>,
              <span className="font-mono text-xs truncate block max-w-[22rem]" title={r.path}>{r.path}</span>,
              <span className={r.status_code && r.status_code >= 400 ? 'text-red-600' : 'text-gray-700'}>{r.status_code ?? '—'}</span>,
              r.duration_ms ?? '—',
              r.ip_address || '—',
            ])}
          />
        </>
      )}

      {/* Page Activity */}
      {tab === 'pages' && (
        <>
          {pages.top_pages.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Most-visited pages</div>
              <div className="flex flex-wrap gap-2">
                {pages.top_pages.map((t, i) => (
                  <span key={i} className="text-xs bg-gray-100 rounded-full px-2.5 py-1 text-gray-700">
                    {t.page_title || t.path} · {t.views} views · {t.distinct_users} users
                  </span>
                ))}
              </div>
            </div>
          )}
          <Table
            empty="No page activity in this window (data accrues as users navigate)."
            head={['User', 'When', 'What', 'Page', 'Section / field']}
            rows={pages.events.map(e => [
              who(e),
              fmt(e.occurred_at),
              EVENT_LABELS[e.event_type] || e.event_type,
              e.page_title || e.path || '—',
              e.event_type === 'page_view' ? '—' : (e.action_name || '—'),
            ])}
          />
        </>
      )}

      {/* Tracked Actions catalog — registry (trackedActions.ts) joined with usage. */}
      {tab === 'actions' && (
        <>
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Named actions instrumented via <code className="bg-gray-200 px-1 rounded">trackAction()</code>.
            Each fires to Datadog RUM (granular / session replay) <strong>and</strong> here. Add a new one in{' '}
            <code className="bg-gray-200 px-1 rounded">src/lib/trackedActions.ts</code> then call it at the code site.
          </div>
          <Table
            empty="No tracked actions defined."
            head={['Action', 'Category', 'What it tracks', 'Count', 'Users', 'Last fired', 'Status']}
            rows={buildActionRows(actionCatalog)}
          />
        </>
      )}

      {/* Issues — errors the user hit (backend 5xx / frontend), LLM-triaged, RCA-linked. */}
      {tab === 'issues' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live · auto-refresh 20s
            </span>
            {[
              ['critical', issueSummary.critical, 'bg-red-100 text-red-700'],
              ['high', issueSummary.high, 'bg-orange-100 text-orange-700'],
              ['medium', issueSummary.medium, 'bg-amber-100 text-amber-700'],
              ['low', issueSummary.low, 'bg-sky-100 text-sky-700'],
              ['untriaged', issueSummary.untriaged, 'bg-gray-100 text-gray-600'],
            ].map(([label, count, cls]) => (
              <span key={String(label)} className={`text-xs px-2.5 py-1 rounded-full ${cls}`}>
                {String(label)}: <strong>{Number(count || 0)}</strong>
              </span>
            ))}
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Errors this user/org actually hit — captured from backend 5xx and the frontend, deduped, and
            triaged by the observability agent. High/critical issues are auto-handed to the RCA pipeline
            and internal ops are alerted.
          </div>
          <Table
            empty="No issues in this window. 🎉"
            head={['Severity', 'Source', 'Issue', 'Count', 'First / Last seen', 'RCA', 'Triage']}
            rows={issues.map((it) => [
              <SeverityBadge severity={it.severity} />,
              <span className="text-xs text-gray-600">{it.source}</span>,
              <div className="max-w-[24rem]">
                <div className="font-mono text-xs text-gray-900 truncate" title={it.path || ''}>
                  {it.method ? `${it.method} ` : ''}{it.path || it.error_type || '—'}
                  {it.status_code ? <span className="text-red-600"> · {it.status_code}</span> : null}
                </div>
                {it.error_message && (
                  <div className="text-xs text-gray-500 truncate" title={it.error_message}>{it.error_message}</div>
                )}
              </div>,
              <span className="font-semibold text-gray-900">{it.occurrence_count}</span>,
              <span className="text-xs text-gray-500">{fmt(it.first_seen_at)}<br />{fmt(it.last_seen_at)}</span>,
              <RcaBadge issue={it} />,
              <span className="text-xs text-gray-600 max-w-[20rem] block" title={it.triage_summary || ''}>
                {it.triage_summary || (it.severity ? '—' : 'pending triage')}
              </span>,
            ])}
          />
        </>
      )}
    </div>
  );
}

const SEVERITY_META: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-sky-100 text-sky-700',
};
function SeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">untriaged</span>;
  const cls = SEVERITY_META[severity] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{severity}</span>;
}

function RcaBadge({ issue }: { issue: IssueRow }) {
  if (!issue.rca_job_id) return <span className="text-xs text-gray-400">—</span>;
  const label = issue.rca_status || 'queued';
  const cls = label === 'done' || label === 'reviewed'
    ? 'bg-emerald-100 text-emerald-700'
    : label === 'awaiting_review'
      ? 'bg-indigo-100 text-indigo-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`} title={issue.rca_branch || ''}>
      RCA: {label}
    </span>
  );
}

/** Merge the static registry with live usage counts; surface unregistered actions too. */
function buildActionRows(catalog: any[]): React.ReactNode[][] {
  const counts: Record<string, any> = {};
  for (const c of catalog) counts[c.action_name] = c;
  const seen = new Set<string>();
  const rows: React.ReactNode[][] = [];

  // Registered actions first (show even at 0).
  for (const def of TRACKED_ACTIONS) {
    seen.add(def.key);
    const u = counts[def.key];
    rows.push([
      <span className="font-medium text-gray-900">{def.label}</span>,
      def.category,
      <span className="text-xs text-gray-500">{def.description}</span>,
      u?.count ?? 0,
      u?.distinct_users ?? 0,
      u ? fmt(u.last_fired) : '—',
      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Registered</span>,
    ]);
  }
  // Any action_name seen in the data but not registered.
  for (const c of catalog) {
    if (seen.has(c.action_name)) continue;
    rows.push([
      <span className="font-mono text-xs text-gray-900">{c.action_name}</span>,
      '—',
      <span className="text-xs text-gray-400">Not in registry — add it to trackedActions.ts</span>,
      c.count,
      c.distinct_users,
      fmt(c.last_fired),
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Unregistered</span>,
    ]);
  }
  return rows;
}

const SOURCE_META: Record<string, { label: string; cls: string }> = {
  frontend: { label: 'Frontend', cls: 'bg-sky-100 text-sky-700' },
  direct: { label: 'Direct API', cls: 'bg-purple-100 text-purple-700' },
  sandbox_key: { label: 'Sandbox key', cls: 'bg-amber-100 text-amber-700' },
  prod_key: { label: 'Prod key', cls: 'bg-emerald-100 text-emerald-700' },
};

function SourceBadge({ source }: { source: string | null }) {
  const meta = SOURCE_META[source || ''] || { label: source || 'Frontend', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>;
}

function Table({ head, rows, empty }: { head: string[]; rows: React.ReactNode[][]; empty: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {head.map(h => (
                <th key={h} className="text-left font-medium text-gray-500 px-4 py-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={head.length} className="px-4 py-8 text-center text-gray-400">{empty}</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {r.map((cell, j) => <td key={j} className="px-4 py-2 align-top text-gray-700">{cell}</td>)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
